/**
 * SA / 社員 担当商談一覧 (v3.2.12 / SA-2 → v3.2.14 で 3 タブ構成に拡張)。
 *
 * URL: /sa/deals
 *
 * タブ:
 *  - 商談: `Deal.assigneeUid === currentUid` または `Deal.salesAgentUid === currentUid` の商談のみ
 *  - 請求可能: 自分の sales_records (status='pending', recipientType='sa') を集約・複数選択して請求書発行
 *  - 発行済請求書: 自分が発行した commission_invoices の履歴
 *
 * 2026-10-30: タブ (?saTab=) と 絞り込み (?saFilter=) を URL に持たせ、商談詳細を開く時 (行・起票後) は戻り先 (担当商談) を渡す。
 *   商談詳細の「前の画面へ」で、同じタブ・絞り込みのままここへ戻れる。
 */
import type { DealStage } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import { Briefcase, FileText, Plus, ReceiptText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useDeals } from '@/hooks/useDeals';
import { useSaContacts } from '@/hooks/useMailContacts';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useUrlEnumParam } from '@/hooks/useUrlParam';
import { functions } from '@/lib/firebase';
import { paths } from '@/routes/paths';

import { SaInvoiceableTab } from './SaInvoiceableTab';
import { SaInvoicesTab } from './SaInvoicesTab';

const createDealBySaFn = httpsCallable<
  {
    contactId: string;
    title?: string;
    stage?: 'briefing' | 'quote';
    memo?: string;
  },
  { ok: true; dealId: string }
>(functions, 'createDealBySa');

type TopTab = 'deals' | 'invoiceable' | 'invoices';

const TOP_TABS: Array<{ value: TopTab; label: string; icon: typeof Briefcase }> = [
  { value: 'deals', label: '商談', icon: Briefcase },
  { value: 'invoiceable', label: '請求可能', icon: ReceiptText },
  { value: 'invoices', label: '発行済請求書', icon: FileText },
];

const STAGE_LABEL: Record<DealStage, string> = {
  document_requested: '資料請求',
  briefing: '個別説明',
  quote: '見積',
  won: '成約',
  agreed: '同意',
  paid: '入金',
  ordered: '発注',
  delivered: '納品',
  lost: '失注',
};

const FILTER_OPTIONS: Array<{ value: 'open' | 'closed' | 'all'; label: string }> = [
  { value: 'open', label: '進行中' },
  { value: 'closed', label: '完了/失注' },
  { value: 'all', label: 'すべて' },
];

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

const TOP_TAB_VALUES = TOP_TABS.map((t) => t.value);
const FILTER_VALUES = FILTER_OPTIONS.map((o) => o.value);

export function SaDealsPage() {
  const { user } = useAuth();
  const allDeals = useDeals();
  const navigate = useNavigate();
  const myContacts = useSaContacts(user?.uid);
  const [topTab, setTopTab] = useUrlEnumParam('saTab', TOP_TAB_VALUES, 'deals');
  const [filter, setFilter] = useUrlEnumParam('saFilter', FILTER_VALUES, 'open');
  const returnToState = useReturnToState('担当商談');

  // 新規商談 dialog 状態
  const [newOpen, setNewOpen] = useState(false);
  const [newContactId, setNewContactId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newStage, setNewStage] = useState<'briefing' | 'quote'>('briefing');
  const [newMemo, setNewMemo] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreateDeal = async () => {
    if (!newContactId) {
      toast.error('顧客を選択してください');
      return;
    }
    setCreating(true);
    try {
      const r = await createDealBySaFn({
        contactId: newContactId,
        title: newTitle.trim() || undefined,
        stage: newStage,
        memo: newMemo.trim() || undefined,
      });
      toast.success('商談を起票しました');
      setNewOpen(false);
      setNewContactId('');
      setNewTitle('');
      setNewStage('briefing');
      setNewMemo('');
      // 起票した Deal の詳細へ遷移
      navigate(paths.dealDetail.replace(':id', r.data.dealId), { state: returnToState });
    } catch (err) {
      toast.error(
        `失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setCreating(false);
    }
  };

  const myDeals = useMemo(() => {
    const uid = user?.uid;
    if (!uid) return [];
    return (allDeals.data ?? []).filter(
      (d) => d.assigneeUid === uid || d.salesAgentUid === uid,
    );
  }, [allDeals.data, user?.uid]);

  const filtered = useMemo(() => {
    if (filter === 'all') return myDeals;
    if (filter === 'open') return myDeals.filter((d) => d.status === 'open');
    return myDeals.filter((d) => d.status === 'closed');
  }, [myDeals, filter]);

  return (
    <Screen
      title="担当商談"
      description="あなたが担当 (assigneeUid) または SA (salesAgentUid) として紐付く商談 + 報酬請求の管理。"
      restoreScroll
      actions={
        <Button variant="primary" onClick={() => setNewOpen(true)}>
          <Plus className="size-3.5" />
          新規商談を起票
        </Button>
      }
    >
      <div className="space-y-3">
        {/* タブ切替 */}
        <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-1">
          {TOP_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTopTab(t.value)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
                  topTab === t.value
                    ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-medium'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <Icon className="size-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {topTab === 'invoiceable' ? (
          <SaInvoiceableTab onIssued={() => setTopTab('invoices')} />
        ) : topTab === 'invoices' ? (
          <SaInvoicesTab />
        ) : (
          <div className="space-y-3">
        <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-2">
          <span className="text-[10px] text-[var(--text-muted)] mr-2">フィルタ:</span>
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFilter(opt.value)}
              className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                filter === opt.value
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {allDeals.isLoading ? (
          <Spinner />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="該当商談なし"
            description="あなたが担当の商談がここに表示されます。"
          />
        ) : (
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <div className="grid grid-cols-[2fr_100px_1fr_100px_100px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              <div>商談</div>
              <div>ステージ</div>
              <div>主軸カテゴリ</div>
              <div className="text-right">期待金額</div>
              <div>最終更新</div>
            </div>
            {filtered.map((d) => (
              <Link
                key={d.id}
                to={paths.dealDetail.replace(':id', d.id)}
                state={returnToState}
                className="grid grid-cols-[2fr_100px_1fr_100px_100px] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)] hover:bg-[var(--bg-hover)]"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate text-[var(--text-primary)]">
                    {d.title}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] truncate">
                    {d.contactKind === 'btob' ? 'BtoB' : 'BtoC'}
                  </div>
                </div>
                <div>
                  <Badge
                    variant={
                      d.stage === 'won' || d.stage === 'paid' || d.stage === 'delivered'
                        ? 'approved'
                        : d.stage === 'lost'
                          ? 'default'
                          : 'review'
                    }
                  >
                    {STAGE_LABEL[d.stage] ?? d.stage}
                  </Badge>
                </div>
                <div className="truncate text-[var(--text-secondary)]">
                  {d.primaryCategory ?? '—'}
                </div>
                <div className="text-right tabular-nums text-[var(--text-secondary)]">
                  {d.expectedAmount != null
                    ? `¥${d.expectedAmount.toLocaleString()}`
                    : '—'}
                </div>
                <div className="text-[var(--text-muted)] tabular-nums">
                  {fmtDate(d.lastTouchedAt)}
                </div>
              </Link>
            ))}
          </div>
        )}
          </div>
        )}
      </div>

      {/* 新規商談起票ダイアログ */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>新規商談を起票</DialogTitle>
            <DialogDescription>
              あなたが担当の顧客 (ownerSaUid=あなた) に対して商談を起こします。salesAgentUid は自動的にあなたに、担当者 (assigneeUid) は社内営業事務 (既定値) にセットされます。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">顧客 *</Label>
              <select
                value={newContactId}
                onChange={(e) => setNewContactId(e.target.value)}
                className="w-full px-2 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">選択してください</option>
                {(myContacts.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || '(名前なし)'} — {c.defaultEmail}
                  </option>
                ))}
              </select>
              {(myContacts.data ?? []).length === 0 && (
                <p className="text-[10px] text-amber-700 mt-1">
                  担当顧客がいません。先に「自分の顧客」から新規追加してください。
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">商談タイプ *</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNewStage('briefing')}
                  className={`flex-1 px-3 py-2 text-xs rounded-md border transition-colors ${
                    newStage === 'briefing'
                      ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                      : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  個別説明
                </button>
                <button
                  type="button"
                  onClick={() => setNewStage('quote')}
                  className={`flex-1 px-3 py-2 text-xs rounded-md border transition-colors ${
                    newStage === 'quote'
                      ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                      : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  見積依頼
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs">商談タイトル (任意)</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="未入力の場合は自動生成されます"
              />
            </div>

            <div>
              <Label className="text-xs">メモ (任意)</Label>
              <Input
                value={newMemo}
                onChange={(e) => setNewMemo(e.target.value)}
                placeholder="例: 紹介経緯 / 興味のある商品 など"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setNewOpen(false)}
              disabled={creating}
            >
              キャンセル
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateDeal}
              disabled={creating || !newContactId}
            >
              {creating ? '起票中…' : '起票'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Screen>
  );
}
